import React, { useState, useEffect } from 'react';
import { useTranslation } from 'next-i18next';
import PropTypes from 'prop-types';

import { theme } from '@spa/shared/theme';
import { ThunderIcon } from '@spa/shared/svg';
import SaveMoney from '@spa/shared/svg/save-money';
import PersonPlus from '@spa/shared/svg/person-plus';
import CalendarInfo from '@spa/shared/svg/calendar-info';
import CalendarLock from '@spa/shared/svg/calendar-lock';
import { formatMoneyPerCountry } from '@spa/shared/utils';
import StatusBanner from '@spa/shared/components/Unisystem/StatusBanner';

export const getNotifications = (validation, t, { isInstantBooking, extraPrice, setMoveInOut }) => [
  ...(isInstantBooking
    ? [
        {
          type: 'extra-data',
          icon: <ThunderIcon height={24} color={theme.colors.uniBlue} />,
          message: 'Instant Booking: If you book this place now your book will be confirmed instantly',
          color: 'blue',
          isVisible: true,
          hasClose: true
        }
      ]
    : []),
  ...(extraPrice
    ? [
        {
          type: 'extra-data',
          icon: <PersonPlus />,
          message:
            'Adding another tenant changes the rental price. <span>Rent will be increased by {{price}} a month.</span>',
          messageParams: {
            price: formatMoneyPerCountry(extraPrice.amount, extraPrice.currency_code)
          },
          color: 'blue',
          isVisible: true,
          hasClose: true
        }
      ]
    : []),
  ...(!validation.isValid && validation.error
    ? [
        {
          type: 'validation',
          message: validation.error,
          color: 'red',
          icon: <CalendarLock />,
          hasClose: true,
          isRepeatable: true
        }
      ]
    : []),
  ...(validation.isValid && validation.warning
    ? [
        {
          type: 'validation',
          message: validation.warning,
          color: 'yellow',
          icon: <CalendarInfo />,
          hasClose: true,
          isRepeatable: true
        }
      ]
    : []),
  ...(!validation.error && validation?.suggestions?.dates
    ? [
        {
          type: 'validation',
          icon: <SaveMoney />,
          message: validation.suggestions.message,
          action: {
            cancel: {
              message: t('I’m ok with my dates')
            },
            confirm: {
              message: t('Change dates'),
              onClick: () => setMoveInOut(validation.suggestions.dates.moveIn, validation.suggestions.dates.moveOut)
            }
          },
          color: 'green'
        }
      ]
    : [])
];

const Notifications = ({ validations, isMobile, extraData }) => {
  const { t } = useTranslation();
  const [cachedValidations, setCachedValidations] = useState(JSON.stringify(validations));
  const [cachedExtraData, setCachedExtraData] = useState(JSON.stringify(extraData));
  const [notifications, setNotifications] = useState(getNotifications(validations, t, extraData));
  const [notification, setNotification] = useState(notifications[notifications.length - 1] ?? null);

  useEffect(() => {
    const hasValidationChanged = JSON.stringify(validations) !== cachedValidations;
    const hasExtraDataChanged = JSON.stringify(extraData) !== cachedExtraData;

    const newNotifications = getNotifications(validations, t, extraData);
    const validationNotifications = newNotifications.filter(({ type, isRepeatable = null }) =>
      hasValidationChanged ? type === 'validation' : type === 'validation' && !!isRepeatable
    );
    const extraDataNotifications = newNotifications.filter(({ type, isRepeatable = null }) =>
      hasExtraDataChanged ? type === 'extra-data' : type === 'extra-data' && !!isRepeatable
    );

    if (!validationNotifications.length && !extraDataNotifications.length) {
      return;
    }

    hasValidationChanged && setCachedValidations(JSON.stringify(validations));

    if (hasExtraDataChanged) {
      setCachedExtraData(JSON.stringify(extraData));
      setNotifications([...extraDataNotifications, ...validationNotifications]);

      return;
    }

    setNotifications([...validationNotifications, ...extraDataNotifications]);
  }, [validations, extraData]);

  useEffect(() => {
    setNotification(notifications[notifications.length - 1] ?? null);
  }, [notifications]);

  const onClose = deleteIndex => {
    setNotifications(notifications.filter((_, index) => index !== deleteIndex));
  };

  if (!notification) {
    return null;
  }

  const { action, icon, color, message, messageParams, hasClose } = notification;

  const confirmAction = action
    ? {
        ...action,
        confirm: {
          ...action.confirm,
          onClick: () => {
            onClose(notifications.length - 1);
            action.confirm.onClick();
          }
        }
      }
    : action;

  return (
    <StatusBanner
      animate
      icon={icon}
      color={color}
      message={messageParams ? t(message, messageParams) : t(message)}
      isFixed={isMobile}
      isMobile={isMobile}
      key={`status-${message}`}
      action={confirmAction}
      hasClose={hasClose}
      onClose={() => onClose(notifications.length - 1)}
    />
  );
};

Notifications.propTypes = {
  validations: PropTypes.object,
  extraData: PropTypes.object,
  isMobile: PropTypes.bool
};

export default Notifications;
